import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useGlobalStore } from '@/store/storeGlobal';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useRefreshStore } from '@/store/Setup/refreshStore';
import {
  addTableColumn as addTableColumnApi,
  addColumnPreflight as addColumnPreflightApi,
  deleteTableColumn as deleteTableColumnApi,
  updateColumnLength,
  setColumnNull,
  setColumnNotNull,
  requiredColumnPreflight,
  requiredColumnApply,
  type NewColumnRequest,
  type AddColumnResult,
  type AddColumnPreflightResult,
  type DefaultFunction
} from '@/api/endpoints/tableColumns';
import {
  fetchSchemas as fetchSchemasApi,
  createSchema as createSchemaApi,
  updateSchema as updateSchemaApi,
  deleteSchema as deleteSchemaApi,
  fetchTables as fetchTablesApi,
  type Schema,
  type TableObject
} from '@/api/endpoints/databaseSchema';

export const useDatabaseSchemaStore = defineStore('databaseSchemaStore', () => {
  const globalStore = useGlobalStore();
  const notificationsStore = useNotificationsStore();
  const refreshStore = useRefreshStore();
  const debugPrefix = '[DatabaseSchema]';

  // Schema related
  const allSchemas = ref<Schema[]>([]);
  const schemas = computed(() => allSchemas.value.filter(schema => !schema.hidden));
  const tables = ref<TableObject[] | null>(null);
  const sysTableColumns = ref<unknown>(null);
  const tableColumnValues = ref<unknown>(null);
  const tableSetupColumns = ref<unknown>(null);

  // Loading and selection state
  const isLoading = ref(false);
  const selectedTableDefinitionId = ref<number | null>(null);

  async function fetchSchemas (): Promise<void> {
    allSchemas.value = await fetchSchemasApi();
  }

  async function createSchema (schemaName: string): Promise<void> {
    allSchemas.value = await createSchemaApi(schemaName);
  }

  async function updateSchema (schemaName: string, hide: boolean): Promise<void> {
    try {
      allSchemas.value = await updateSchemaApi(schemaName, hide);
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
      if (axiosError.response?.status === 403) {
        notificationsStore.addNotification({
          type: 'error',
          title: 'Kan ikke endre systemskjema',
          message: axiosError.response?.data?.detail || 'Systemskjema kan ikke endres eller slettes'
        });
      } else {
        throw error;
      }
    }
  }

  async function deleteSchema (schemaName: string): Promise<void> {
    try {
      allSchemas.value = await deleteSchemaApi(schemaName);
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
      if (axiosError.response?.status === 403) {
        notificationsStore.addNotification({
          type: 'error',
          title: 'Kan ikke slette systemskjema',
          message: axiosError.response?.data?.detail || 'Systemskjema kan ikke endres eller slettes'
        });
      } else {
        throw error;
      }
    }
  }

  async function fetchTables (schemaName: string): Promise<void> {
    isLoading.value = true;
    try {
      tables.value = await fetchTablesApi(schemaName);
    } catch (error) {
      console.error('Error fetching tables:', error);
      notificationsStore.addError('Kunne ikke laste tabeller');
      tables.value = null;
    } finally {
      isLoading.value = false;
    }
  }

  function setSelectedTableDefinition (tableDefinitionId: number): void {
    selectedTableDefinitionId.value = tableDefinitionId;
  }

  // Table Column operations
  async function addTableColumn (
    tableDefinitionId: number,
    tableColumn: NewColumnRequest
  ): Promise<AddColumnResult> {
    globalStore.debug(`${debugPrefix} addTableColumn`, tableColumn);

    const result = await addTableColumnApi(tableDefinitionId, tableColumn);
    if (result.success) {
      notificationsStore.addSuccess('Kolonne lagt til');
      refreshStore.triggerRefresh();
    }
    return result;
  }

  async function deleteTableColumn (tableDefinitionId: number, columnName: string): Promise<void> {
    globalStore.debug(`${debugPrefix} deleteTableColumn`, columnName, tableDefinitionId);

    await deleteTableColumnApi(tableDefinitionId, columnName);
    notificationsStore.addSuccess('Tabell oppdatert', 'Kolonne er slettet i databasen');
    refreshStore.triggerRefresh();
  }

  async function changeTableColumnUpdateLength (
    tableDefinitionId: number,
    columnName: string,
    newDataType: string
  ): Promise<void> {
    await updateColumnLength({ tableDefinitionId, columnName, newDataType });
    notificationsStore.addSuccess('Lengden på datatypen er endret');
    refreshStore.triggerRefresh();
  }

  async function changeTableColumnSetNull (
    tableDefinitionId: number,
    columnName: string
  ): Promise<void> {
    await setColumnNull({ tableDefinitionId, columnName });
    notificationsStore.addSuccess('Kolonnen er endret');
    refreshStore.triggerRefresh();
  }

  async function changeTableColumnSetNotNull (
    tableDefinitionId: number,
    columnName: string,
    defaultFunction?: DefaultFunction
  ): Promise<void> {
    await setColumnNotNull({ tableDefinitionId, columnName, defaultFunction });
    notificationsStore.addSuccess('Kolonnen er endret');
    refreshStore.triggerRefresh();
  }

  // Required column flow (preflight + apply)
  async function requiredPreflight (
    tableDefinitionId: number,
    columnName: string
  ) {
    return requiredColumnPreflight({ tableDefinitionId, columnName });
  }

  async function requiredApply (
    tableDefinitionId: number,
    columnName: string,
    fillValue?: string | null
  ): Promise<void> {
    await requiredColumnApply({ tableDefinitionId, columnName, fillValue });
    notificationsStore.addSuccess('Kolonnen er satt som påkrevd');
    refreshStore.triggerRefresh();
  }

  /**
   * Preflight for AddColumn - sjekker om tabellen har data.
   */
  async function addColumnPreflight (
    tableDefinitionId: number,
    nullHandling: string
  ): Promise<AddColumnPreflightResult> {
    return addColumnPreflightApi(tableDefinitionId, nullHandling);
  }

  function findTableDefinitionId (fqnTableName: string): number | null {
    const dbTable = tables.value?.find(t => `${t.schemaName}.${t.objectName}` === fqnTableName);
    return dbTable?.tableDefinitionId ?? null;
  }

  return {
    // State
    schemas,
    allSchemas,
    tables,
    sysTableColumns,
    tableColumnValues,
    tableSetupColumns,
    isLoading,
    selectedTableDefinitionId,

    // Methods
    fetchSchemas,
    fetchTables,
    addTableColumn,
    addColumnPreflight,
    deleteTableColumn,
    changeTableColumnUpdateLength,
    changeTableColumnSetNull,
    changeTableColumnSetNotNull,
    requiredPreflight,
    requiredApply,
    createSchema,
    updateSchema,
    deleteSchema,
    setSelectedTableDefinition,
    findTableDefinitionId,
  };
});

